import React, { useEffect, useState, Children } from 'react';
import { motion } from 'framer-motion';
import { ArrowRightIcon, CheckIcon, CpuIcon, WrenchIcon, ShieldIcon, RefreshCwIcon, EyeIcon, BrainIcon, FileTextIcon, AlertTriangleIcon, UsersIcon, BuildingIcon, ZapIcon, LayersIcon, SearchIcon, PaletteIcon } from 'lucide-react';
import { CTABanner } from '../../components/CTABanner';
const fadeUp = {
  hidden: {
    opacity: 0,
    y: 20
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.5
    }
  }
};
const stagger = {
  hidden: {},
  visible: {
    transition: {
      staggerChildren: 0.1
    }
  }
};
function SectionDivider() {
  return <div className="absolute top-0 left-1/2 -translate-x-1/2 w-1/2 h-px bg-gradient-to-r from-transparent via-violet-electric/20 to-transparent" data-id="element-1064" />;
}
export function CustomPage() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  return <div className="min-h-screen w-full bg-navy" data-id="element-1065">
      {/* ── SECTION 1: HERO ── */}
      <section className="relative w-full min-h-screen flex items-center justify-center pt-32 pb-24 px-6 overflow-hidden bg-navy" data-id="element-1066">
        {/* Background glow */}
        <div className="absolute inset-0 pointer-events-none" style={{
        background: 'radial-gradient(ellipse 80% 60% at 50% 0%, rgba(108,92,231,0.22) 0%, rgba(200,80,192,0.07) 50%, transparent 80%)'
      }} data-id="element-1067" />
        <div className="absolute bottom-0 left-0 right-0 h-32 pointer-events-none" style={{
        background: 'linear-gradient(to top, #0D0B2E, transparent)'
      }} data-id="element-1068" />

        <div className="relative z-10 max-w-3xl mx-auto text-center" data-id="element-1069">
          <motion.span className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-violet-electric/30 bg-violet-electric/10 text-violet-electric text-xs font-semibold tracking-[0.15em] uppercase mb-6" initial={{
          opacity: 0,
          y: 12
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.4,
          delay: 0.1
        }} data-id="element-1070">
            Thruline Core
          </motion.span>

          <motion.h1 className="text-5xl sm:text-6xl md:text-7xl font-bold text-white leading-[1.05] tracking-tight mb-6" initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.55,
          delay: 0.2
        }} data-id="element-1071">
            A custom-developed ERP.
            <br data-id="element-1072" />
            <span className="text-transparent bg-clip-text" style={{
            backgroundImage: 'linear-gradient(90deg, #a78bfa, #C850C0)'
          }} data-id="element-1073">
              No developers needed.
            </span>
          </motion.h1>

          <motion.p className="text-lg sm:text-xl text-white/55 max-w-2xl mx-auto mb-10 leading-relaxed" initial={{
          opacity: 0,
          y: 16
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.5,
          delay: 0.3
        }} data-id="element-1074">
            Thruline helps you plan and build a system configured to your
            actual operations. You own it, understand every workflow and rule,
            and evolve it yourself as your business grows — with AI assistance
            and expert advisory when you need it.
          </motion.p>

          <motion.div className="flex flex-col sm:flex-row items-center justify-center gap-4" initial={{
          opacity: 0,
          y: 16
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.5,
          delay: 0.4
        }} data-id="element-1075">
            <a href="https://calendly.com/alex-thruline/learn-about-thruline" className="inline-flex items-center gap-2 px-8 py-3.5 rounded-full bg-white text-navy font-semibold text-sm hover:bg-white/90 transition-all hover:shadow-lg hover:shadow-white/10" data-id="element-1076">
              See how it works
              <ArrowRightIcon className="w-4 h-4" data-id="element-1077" />
            </a>
            <a href="https://calendly.com/alex-thruline/learn-about-thruline" className="inline-flex items-center px-8 py-3.5 rounded-full border border-white/25 text-white font-medium text-sm hover:border-white/50 transition-all" data-id="element-1078">
              Talk to our team
            </a>
          </motion.div>
        </div>
      </section>

      {/* ── SECTION 2: THE PROBLEM ── */}
      <section className="relative w-full bg-navy-light py-24 px-6" data-id="element-1079">
        <SectionDivider data-id="element-1080" />
        <div className="max-w-3xl mx-auto" data-id="element-1081">
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: '-60px'
        }} variants={stagger} data-id="element-1082">
            <motion.span variants={fadeUp} className="text-violet-electric text-xs font-semibold tracking-[0.2em] uppercase block mb-4" data-id="element-1083">
              The Problem
            </motion.span>
            <motion.h2 variants={fadeUp} className="text-3xl sm:text-4xl font-bold text-white tracking-tight mb-12" data-id="element-1084">
              You know your business.
              <br data-id="element-1085" />
              Your ERP doesn't.
            </motion.h2>

            <div className="space-y-8" data-id="element-1086">
              <motion.div variants={fadeUp} className="flex gap-5" data-id="element-1087">
                <div className="flex-shrink-0 w-8 h-8 rounded-full bg-red-500/10 border border-red-500/20 flex items-center justify-center mt-0.5" data-id="element-1088">
                  <span className="text-red-400 text-xs font-bold" data-id="element-1089">1</span>
                </div>
                <div data-id="element-1090">
                  <p className="text-white/70 text-base leading-relaxed" data-id="element-1091">
                    <span className="text-white font-medium" data-id="element-1092">
                      Off-the-shelf ERP forces you to work the way the software
                      was designed
                    </span>
                    , not the way your operation actually runs. You end up with
                    workarounds, shadow spreadsheets, and processes that exist
                    only because the system can't handle how you actually do
                    things.
                  </p>
                </div>
              </motion.div>

              <motion.div variants={fadeUp} className="flex gap-5" data-id="element-1093">
                <div className="flex-shrink-0 w-8 h-8 rounded-full bg-amber-500/10 border border-amber-500/20 flex items-center justify-center mt-0.5" data-id="element-1094">
                  <span className="text-amber-400 text-xs font-bold" data-id="element-1095">2</span>
                </div>
                <div data-id="element-1096">
                  <p className="text-white/70 text-base leading-relaxed" data-id="element-1097">
                    <span className="text-white font-medium" data-id="element-1098">
                      Hiring a team to build something custom solves the fit
                      problem
                    </span>{' '}
                    — but requires developers, implementation consultants, and
                    ongoing technical staff that most manufacturers can't
                    justify. The cost of having your own system has always been
                    the cost of having your own team.
                  </p>
                </div>
              </motion.div>

              <motion.div variants={fadeUp} className="flex gap-5" data-id="element-1099">
                <div className="flex-shrink-0 w-8 h-8 rounded-full bg-blue-500/10 border border-blue-500/20 flex items-center justify-center mt-0.5" data-id="element-1100">
                  <span className="text-blue-400 text-xs font-bold" data-id="element-1101">3</span>
                </div>
                <div data-id="element-1102">
                  <p className="text-white/70 text-base leading-relaxed" data-id="element-1103">
                    <span className="text-white font-medium" data-id="element-1104">
                      Handing it to a managed service provider gets you a custom
                      system
                    </span>{' '}
                    — but you trade one dependency for another. Every change
                    goes through their queue. Every modification is on their
                    timeline. You use the system, but you never truly own it.
                  </p>
                </div>
              </motion.div>
            </div>

            <motion.div variants={fadeUp} className="mt-12 pt-8 border-t border-white/[0.06]" data-id="element-1105">
              <p className="text-xl font-semibold text-white" data-id="element-1106">
                Thruline Core gives you the system without the team — and the
                control without the dependency.
              </p>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* ── SECTION 3: WHAT IS THRULINE CORE ── */}
      <section className="relative w-full bg-navy py-24 px-6" data-id="element-1107">
        <SectionDivider data-id="element-1108" />
        <div className="max-w-5xl mx-auto" data-id="element-1109">
          <motion.div className="text-center mb-16" initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: '-60px'
        }} variants={stagger} data-id="element-1110">
            <motion.span variants={fadeUp} className="text-violet-electric text-xs font-semibold tracking-[0.2em] uppercase block mb-4" data-id="element-1111">
              What Is Thruline Core
            </motion.span>
            <motion.h2 variants={fadeUp} className="text-3xl sm:text-4xl font-bold text-white tracking-tight max-w-2xl mx-auto" data-id="element-1112">
              Your own operating system. Built together. Owned by you.
            </motion.h2>
          </motion.div>

          <motion.div className="grid grid-cols-1 md:grid-cols-2 gap-5" initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: '-40px'
        }} variants={stagger} data-id="element-1113">
            {/* A: Custom-Developed */}
            <motion.div variants={fadeUp} className="rounded-2xl border border-white/[0.06] bg-white/[0.03] p-8" data-id="element-1114">
              <div className="w-10 h-10 rounded-xl bg-violet-electric/15 flex items-center justify-center mb-5" data-id="element-1115">
                <LayersIcon className="w-5 h-5 text-violet-electric" data-id="element-1116" />
              </div>
              <h3 className="text-lg font-semibold text-white mb-3" data-id="element-1117">
                Custom-Developed to Your Operation
              </h3>
              <p className="text-white/55 text-sm leading-relaxed" data-id="element-1118">
                Core isn't a template you configure — it's a platform where
                your actual workflows, rules, and processes become the system.
                Production scheduling, inventory, purchasing, quality, shipping
                — all built around how your operation actually works, not how a
                vendor thinks it should.
              </p>
            </motion.div>

            {/* B: Built Together */}
            <motion.div variants={fadeUp} className="rounded-2xl border border-white/[0.06] bg-white/[0.03] p-8" data-id="element-1119">
              <div className="w-10 h-10 rounded-xl bg-violet-electric/15 flex items-center justify-center mb-5" data-id="element-1120">
                <UsersIcon className="w-5 h-5 text-violet-electric" data-id="element-1121" />
              </div>
              <h3 className="text-lg font-semibold text-white mb-3" data-id="element-1122">
                Thruline Is Your Development Team
              </h3>
              <p className="text-white/55 text-sm leading-relaxed" data-id="element-1123">
                You don't start with a blank screen. Thruline's AI reads your
                existing documents to map your operation, then our team builds
                the first version with you — walking you through every
                workflow, every rule, and why it was configured that way. The
                goal isn't just a working system. It's a working system you
                understand.
              </p>
            </motion.div>

            {/* C: Full Transparency & Ownership */}
            <motion.div variants={fadeUp} className="rounded-2xl border border-white/[0.06] bg-white/[0.03] p-8" data-id="element-1124">
              <div className="w-10 h-10 rounded-xl bg-violet-electric/15 flex items-center justify-center mb-5" data-id="element-1125">
                <EyeIcon className="w-5 h-5 text-violet-electric" data-id="element-1126" />
              </div>
              <h3 className="text-lg font-semibold text-white mb-3" data-id="element-1127">
                Full Transparency. Full Ownership.
              </h3>
              <p className="text-white/55 text-sm leading-relaxed" data-id="element-1128">
                Every workflow, rule, automation, and connection in your system
                is visible. Nothing is hidden in proprietary code or managed
                behind a curtain. You can open any process and see exactly how
                it works, what triggers it, and what documents informed its
                design. You can't evolve what you can't see — so Core shows
                you everything.
              </p>
            </motion.div>

            {/* D: Evolve It Yourself */}
            <motion.div variants={fadeUp} className="rounded-2xl border border-violet-electric/20 bg-violet-electric/[0.04] p-8" data-id="element-1129">
              <div className="w-10 h-10 rounded-xl bg-violet-electric/20 flex items-center justify-center mb-5" data-id="element-1130">
                <RefreshCwIcon className="w-5 h-5 text-violet-electric" data-id="element-1131" />
              </div>
              <h3 className="text-lg font-semibold text-white mb-3" data-id="element-1132">
                Evolve It Yourself
              </h3>
              <p className="text-white/55 text-sm leading-relaxed" data-id="element-1133">
                When your business changes, you change the system — directly.
                Describe what you need in plain language and AI suggests the
                configuration changes. See exactly what each modification
                affects before it goes live. Flag anything for Thruline expert
                review when the stakes are high. No tickets. No waiting. No
                change orders. You hold the wrench.
              </p>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* ── SECTION 4: WHO IT'S FOR ── */}
      <section className="relative w-full bg-navy-light py-24 px-6" data-id="element-1134">
        <SectionDivider data-id="element-1135" />
        <div className="max-w-3xl mx-auto" data-id="element-1136">
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: '-60px'
        }} variants={stagger} data-id="element-1137">
            <motion.span variants={fadeUp} className="text-violet-electric text-xs font-semibold tracking-[0.2em] uppercase block mb-4" data-id="element-1138">
              Who It's For
            </motion.span>
            <motion.h2 variants={fadeUp} className="text-3xl sm:text-4xl font-bold text-white tracking-tight mb-4" data-id="element-1139">
              Built for manufacturers who want to own their system — not just
              use it.
            </motion.h2>
            <motion.p variants={fadeUp} className="text-white/50 text-base mb-10" data-id="element-1140">
              Thruline Core is for you if:
            </motion.p>

            <motion.div variants={stagger} className="space-y-4" data-id="element-1141">
              {["You're a manufacturer with 50–500 employees running operations that have outgrown spreadsheets and basic software", "You have no development team, or a very small one that's already stretched thin", "You've been burned by off-the-shelf ERP that didn't fit, or custom ERP you couldn't maintain", 'You want to understand and modify your system without calling a vendor every time something changes', 'You want the kind of system that only companies with full dev teams and internal consultants can typically build — without hiring them'].map((item, i) => <motion.div key={i} variants={fadeUp} className="flex items-start gap-4" data-id="element-1142">
                  <div className="flex-shrink-0 w-6 h-6 rounded-full bg-violet-electric/15 border border-violet-electric/25 flex items-center justify-center mt-0.5" data-id="element-1143">
                    <CheckIcon className="w-3.5 h-3.5 text-violet-electric" data-id="element-1144" />
                  </div>
                  <p className="text-white/70 text-base leading-relaxed" data-id="element-1145">
                    {item}
                  </p>
                </motion.div>)}
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* ── SECTION 5: HOW IT WORKS ── */}
      <section className="relative w-full bg-navy py-24 px-6" data-id="element-1146">
        <SectionDivider data-id="element-1147" />
        <div className="max-w-4xl mx-auto" data-id="element-1148">
          <motion.div className="text-center mb-16" initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: '-60px'
        }} variants={stagger} data-id="element-1149">
            <motion.span variants={fadeUp} className="text-violet-electric text-xs font-semibold tracking-[0.2em] uppercase block mb-4" data-id="element-1150">
              How It Works
            </motion.span>
            <motion.h2 variants={fadeUp} className="text-3xl sm:text-4xl font-bold text-white tracking-tight" data-id="element-1151">
              Thruline helps you plan and build it. You own it from there.
            </motion.h2>
          </motion.div>

          <motion.div className="space-y-5" initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: '-40px'
        }} variants={stagger} data-id="element-1152">
            {/* Phase 1 */}
            <motion.div variants={fadeUp} className="rounded-2xl border border-white/[0.06] bg-white/[0.02] overflow-hidden" data-id="element-1153">
              <div className="flex items-start gap-6 p-8" data-id="element-1154">
                <div className="flex-shrink-0" data-id="element-1155">
                  <div className="w-12 h-12 rounded-xl bg-violet-electric/15 border border-violet-electric/20 flex items-center justify-center" data-id="element-1156">
                    <BrainIcon className="w-6 h-6 text-violet-electric" data-id="element-1157" />
                  </div>
                </div>
                <div className="flex-1" data-id="element-1158">
                  <div className="flex items-center gap-3 mb-3" data-id="element-1159">
                    <span className="text-violet-electric text-xs font-bold tracking-widest uppercase" data-id="element-1160">
                      Phase 1
                    </span>
                    <span className="text-white/20 text-xs" data-id="element-1161">—</span>
                    <span className="text-white font-semibold" data-id="element-1162">Discovery</span>
                    <span className="ml-auto px-2.5 py-0.5 rounded-full bg-emerald-500/10 border border-emerald-500/20 text-emerald-400 text-[10px] font-bold tracking-wide uppercase" data-id="element-1163">
                      Free
                    </span>
                  </div>
                  <p className="text-white/55 text-sm leading-relaxed mb-4" data-id="element-1164">
                    Thruline's AI reads your existing documents — SOPs,
                    spreadsheets, emails, PDFs — and maps your actual
                    operational processes. Our team reviews the results and
                    builds a configuration blueprint: what your system needs,
                    how it should work, and why.
                  </p>
                  <p className="text-white/40 text-sm" data-id="element-1165">
                    <span className="text-white/60 font-medium" data-id="element-1166">
                      What you get:
                    </span>{' '}
                    A clear picture of your operation translated into system
                    architecture. No guesswork. No months of consultant
                    interviews.
                  </p>
                </div>
              </div>
            </motion.div>

            {/* Phase 2 */}
            <motion.div variants={fadeUp} className="rounded-2xl border border-white/[0.06] bg-white/[0.02] overflow-hidden" data-id="element-1167">
              <div className="flex items-start gap-6 p-8" data-id="element-1168">
                <div className="flex-shrink-0" data-id="element-1169">
                  <div className="w-12 h-12 rounded-xl bg-violet-electric/15 border border-violet-electric/20 flex items-center justify-center" data-id="element-1170">
                    <WrenchIcon className="w-6 h-6 text-violet-electric" data-id="element-1171" />
                  </div>
                </div>
                <div className="flex-1" data-id="element-1172">
                  <div className="flex items-center gap-3 mb-3" data-id="element-1173">
                    <span className="text-violet-electric text-xs font-bold tracking-widest uppercase" data-id="element-1174">
                      Phase 2
                    </span>
                    <span className="text-white/20 text-xs" data-id="element-1175">—</span>
                    <span className="text-white font-semibold" data-id="element-1176">
                      First Build
                    </span>
                    <span className="ml-auto px-2.5 py-0.5 rounded-full bg-violet-electric/10 border border-violet-electric/20 text-violet-electric text-[10px] font-bold tracking-wide uppercase" data-id="element-1177">
                      Fixed Quote
                    </span>
                  </div>
                  <p className="text-white/55 text-sm leading-relaxed mb-4" data-id="element-1178">
                    Thruline builds the initial system with you using the
                    discovery blueprint. Every configuration is transparent,
                    documented, and explained. We walk you through what was
                    built and why, tying each decision back to the documents
                    and processes that informed it. You're not just getting a
                    system — you're learning how it works.
                  </p>
                  <p className="text-white/40 text-sm" data-id="element-1179">
                    <span className="text-white/60 font-medium" data-id="element-1180">
                      What you get:
                    </span>{' '}
                    A working system configured to your operation, on a
                    platform you fully understand and can modify. Not a black
                    box.
                  </p>
                </div>
              </div>
            </motion.div>

            {/* Phase 3 */}
            <motion.div variants={fadeUp} className="rounded-2xl border border-violet-electric/15 bg-violet-electric/[0.03] overflow-hidden" data-id="element-1181">
              <div className="flex items-start gap-6 p-8" data-id="element-1182">
                <div className="flex-shrink-0" data-id="element-1183">
                  <div className="w-12 h-12 rounded-xl bg-violet-electric/20 border border-violet-electric/30 flex items-center justify-center" data-id="element-1184">
                    <RefreshCwIcon className="w-6 h-6 text-violet-electric" data-id="element-1185" />
                  </div>
                </div>
                <div className="flex-1" data-id="element-1186">
                  <div className="flex items-center gap-3 mb-3" data-id="element-1187">
                    <span className="text-violet-electric text-xs font-bold tracking-widest uppercase" data-id="element-1188">
                      Phase 3
                    </span>
                    <span className="text-white/20 text-xs" data-id="element-1189">—</span>
                    <span className="text-white font-semibold" data-id="element-1190">
                      Operate & Evolve
                    </span>
                    <span className="ml-auto px-2.5 py-0.5 rounded-full bg-blue-500/10 border border-blue-500/20 text-blue-400 text-[10px] font-bold tracking-wide uppercase" data-id="element-1191">
                      Platform License
                    </span>
                  </div>
                  <p className="text-white/55 text-sm leading-relaxed mb-4" data-id="element-1192">
                    You run your business on Core. When something changes —
                    new product line, new process, new requirement — you
                    describe the change in plain language and the AI suggests
                    configuration modifications. You see the impact before
                    anything goes live. Flag changes for Thruline expert
                    review when you want a second opinion. You have day-to-day
                    control. Thruline advisory support is there when you need
                    it.
                  </p>
                  <p className="text-white/40 text-sm" data-id="element-1193">
                    <span className="text-white/60 font-medium" data-id="element-1194">
                      What you get:
                    </span>{' '}
                    A system that evolves with your business — on your
                    timeline, not a vendor's. Ongoing control without ongoing
                    dependency.
                  </p>
                </div>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* ── SECTION 6: WHAT MAKES CORE DIFFERENT ── */}
      <section className="relative w-full bg-navy-light py-24 px-6" data-id="element-1195">
        <SectionDivider data-id="element-1196" />
        <div className="max-w-5xl mx-auto" data-id="element-1197">
          <motion.div className="text-center mb-16" initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: '-60px'
        }} variants={stagger} data-id="element-1198">
            <motion.span variants={fadeUp} className="text-violet-electric text-xs font-semibold tracking-[0.2em] uppercase block mb-4" data-id="element-1199">
              What Makes Core Different
            </motion.span>
            <motion.h2 variants={fadeUp} className="text-3xl sm:text-4xl font-bold text-white tracking-tight" data-id="element-1200">
              The capabilities that replace
              <br data-id="element-1201" />
              your development team.
            </motion.h2>
          </motion.div>

          <motion.div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5" initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: '-40px'
        }} variants={stagger} data-id="element-1202">
            {[{
            icon: EyeIcon,
            title: 'Full Configuration Transparency',
            desc: 'Every workflow, rule, automation, and connection is visible. Open any process and see exactly how it works, what triggers it, and what documents informed its design. The system is an open book.'
          }, {
            icon: BrainIcon,
            title: 'AI-Assisted Self-Configuration',
            desc: 'Describe changes in plain operational language. The AI suggests configuration modifications based on your existing setup and discovery data. Review, adjust, deploy — no programming required.'
          }, {
            icon: SearchIcon,
            title: 'Change Impact Visibility',
            desc: 'Before any modification goes live, see exactly what it affects downstream. "Changing this receiving workflow impacts these 3 processes and these 2 reports." No surprises.'
          }, {
            icon: FileTextIcon,
            title: 'Self-Documenting System',
            desc: 'Every configuration traces to its source document or decision. When someone asks "why does this work this way?" the system shows them. Knowledge stays in the system, not in people\'s heads.'
          }, {
            icon: UsersIcon,
            title: 'On-Demand Expert Review',
            desc: 'Flag any modification for Thruline consultant review before deploying. Build it yourself, get an expert eye when the stakes are high. Like a pull request — optional, not required.'
          }, {
            icon: PaletteIcon,
            title: 'Your Brand, Your System',
            desc: 'Core runs under your company\'s identity. Your team logs into your system, not a vendor\'s platform. When your warehouse team sees "YourCompany Operations," they own it.'
          }, {
            icon: LayersIcon,
            title: 'Full Module Suite',
            desc: 'Production scheduling, inventory management, purchasing, receiving, shipping, quality management, field operations, maintenance tracking. Start with what you need most.'
          }].map((item, i) => {
            const IconComp = item.icon;
            return <motion.div key={i} variants={fadeUp} className="rounded-2xl border border-white/[0.06] bg-white/[0.02] p-7" data-id="element-1203">
                  <div className="w-9 h-9 rounded-lg bg-violet-electric/12 flex items-center justify-center mb-4" data-id="element-1204">
                    <IconComp className="w-4.5 h-4.5 text-violet-electric" data-id="element-1205" />
                  </div>
                  <h3 className="text-sm font-semibold text-white mb-2" data-id="element-1206">
                    {item.title}
                  </h3>
                  <p className="text-white/45 text-sm leading-relaxed" data-id="element-1207">
                    {item.desc}
                  </p>
                </motion.div>;
          })}
          </motion.div>
        </div>
      </section>

      {/* ── SECTION 7: COMMON QUESTIONS ── */}
      <section className="relative w-full bg-navy py-24 px-6" data-id="element-1208">
        <SectionDivider data-id="element-1209" />
        <div className="max-w-4xl mx-auto" data-id="element-1210">
          <motion.div className="text-center mb-16" initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: '-60px'
        }} variants={stagger} data-id="element-1211">
            <motion.span variants={fadeUp} className="text-violet-electric text-xs font-semibold tracking-[0.2em] uppercase block mb-4" data-id="element-1212">
              Common Questions
            </motion.span>
            <motion.h2 variants={fadeUp} className="text-3xl sm:text-4xl font-bold text-white tracking-tight" data-id="element-1213">
              Why Core
            </motion.h2>
          </motion.div>

          <motion.div className="grid grid-cols-1 md:grid-cols-2 gap-5" initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: '-40px'
        }} variants={stagger} data-id="element-1214">
            {[{
            q: 'Why not just buy off-the-shelf ERP?',
            a: "Because off-the-shelf systems make you work their way. You end up with workarounds, shadow spreadsheets, and processes that exist because the software can't handle how you actually operate. Core is custom-developed around your operation from day one, and you can adjust it when your operation changes — the same day, without a vendor."
          }, {
            q: 'Why not hire a team to build something custom?',
            a: "Because that requires developers, implementation consultants, and ongoing technical staff that most manufacturers can't justify. Core gives you the result — a system built around your business — without the overhead. Thruline is the development team you don't have to hire."
          }, {
            q: 'Why Core instead of a managed service like Thruline Enterprise?',
            a: "Core is for companies that want to own and evolve their system — not just use it. You get full transparency into every configuration, AI tools to make changes yourself, and the freedom to adjust things on your timeline. Enterprise is great if you want Thruline to handle everything. Core is for when you want the wrench in your own hand."
          }, {
            q: 'What if I break something?',
            a: 'Core shows you the impact of every change before you deploy it. You can see what a modification affects downstream. You can flag any change for Thruline review before it goes live. And every Core customer gets a complete sandbox environment — test any change in full isolation before it touches your live system.'
          }].map((item, i) => <motion.div key={i} variants={fadeUp} className="rounded-2xl border border-white/[0.06] bg-white/[0.02] p-8" data-id="element-1215">
                <h3 className="text-base font-semibold text-white mb-3 leading-snug" data-id="element-1216">
                  "{item.q}"
                </h3>
                <p className="text-white/50 text-sm leading-relaxed" data-id="element-1217">
                  {item.a}
                </p>
              </motion.div>)}
          </motion.div>
        </div>
      </section>

      {/* ── SECTION 8: COMPARISON TABLE ── */}
      <section className="relative w-full bg-navy-light py-24 px-6" data-id="element-1218">
        <SectionDivider data-id="element-1219" />
        <div className="max-w-4xl mx-auto" data-id="element-1220">
          <motion.div className="text-center mb-14" initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: '-60px'
        }} variants={stagger} data-id="element-1221">
            <motion.span variants={fadeUp} className="text-violet-electric text-xs font-semibold tracking-[0.2em] uppercase block mb-4" data-id="element-1222">
              Comparison
            </motion.span>
            <motion.h2 variants={fadeUp} className="text-3xl sm:text-4xl font-bold text-white tracking-tight" data-id="element-1223">
              Thruline Core vs. Traditional ERP
            </motion.h2>
          </motion.div>

          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true,
          margin: '-40px'
        }} transition={{
          duration: 0.5
        }} className="rounded-2xl border border-white/[0.06] overflow-hidden" data-id="element-1224">
            <table className="w-full text-sm" data-id="element-1225">
              <thead data-id="element-1226">
                <tr className="border-b border-white/[0.06]" data-id="element-1227">
                  <th className="text-left px-6 py-4 text-white/30 font-medium text-xs uppercase tracking-wider w-1/3" data-id="element-1228"></th>
                  <th className="text-left px-6 py-4 text-white/40 font-medium text-xs uppercase tracking-wider w-1/3" data-id="element-1229">
                    Traditional ERP
                  </th>
                  <th className="text-left px-6 py-4 text-violet-electric font-semibold text-xs uppercase tracking-wider w-1/3 bg-violet-electric/[0.04]" data-id="element-1230">
                    Thruline Core
                  </th>
                </tr>
              </thead>
              <tbody data-id="element-1231">
                {[["How it's built", 'Vendor configures to their templates', 'Custom-developed around your actual processes'], ['Who understands it', 'The vendor (or consultants)', 'Your team — full transparency into every rule'], ['When you need a change', 'File a ticket. Wait. Pay.', 'Describe it. See the impact. Deploy it.'], ['Documentation', 'Binder from implementation day', 'Self-documenting. Always current.'], ['Your brand', "Vendor's interface with your data", 'Your system, your identity'], ['Ongoing cost model', 'License + change orders + consulting', 'Platform license + advisory as needed'], ['Time to first change', 'Weeks to months', 'Same day'], ['Knowledge retention', 'Walks out the door with employees', 'Built into the system permanently'], ['Who it requires', 'IT dept or vendor dependency', 'Your operations team + Thruline advisory']].map(([label, traditional, core], i) => <tr key={i} className={`border-b border-white/[0.04] ${i % 2 === 0 ? 'bg-white/[0.01]' : ''}`} data-id="element-1232">
                    <td className="px-6 py-4 text-white/50 font-medium" data-id="element-1233">
                      {label}
                    </td>
                    <td className="px-6 py-4 text-white/35" data-id="element-1234">{traditional}</td>
                    <td className="px-6 py-4 text-white/70 bg-violet-electric/[0.03]" data-id="element-1235">
                      {core}
                    </td>
                  </tr>)}
              </tbody>
            </table>
          </motion.div>
        </div>
      </section>

      {/* ── SECTION 9: CORE VS ENTERPRISE ── */}
      <section className="relative w-full bg-navy py-24 px-6" data-id="element-1236">
        <SectionDivider data-id="element-1237" />
        <div className="max-w-4xl mx-auto" data-id="element-1238">
          <motion.div className="text-center mb-14" initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: '-60px'
        }} variants={stagger} data-id="element-1239">
            <motion.span variants={fadeUp} className="text-violet-electric text-xs font-semibold tracking-[0.2em] uppercase block mb-4" data-id="element-1240">
              Two Ways to Work With Thruline
            </motion.span>
            <motion.h2 variants={fadeUp} className="text-3xl sm:text-4xl font-bold text-white tracking-tight mb-4" data-id="element-1241">
              Want to own it, or want it done?
            </motion.h2>
            <motion.p variants={fadeUp} className="text-white/45 text-base max-w-xl mx-auto" data-id="element-1242">
              Both start with the same AI-powered Discovery. The difference is
              who holds the wrench after go-live.
            </motion.p>
          </motion.div>

          <motion.div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-8" initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: '-40px'
        }} variants={stagger} data-id="element-1243">
            {/* Core Card — highlighted */}
            <motion.div variants={fadeUp} className="rounded-2xl border border-violet-electric/20 bg-violet-electric/[0.04] p-8" data-id="element-1244">
              <div className="flex items-center gap-3 mb-4" data-id="element-1245">
                <div className="w-10 h-10 rounded-xl bg-violet-electric/20 flex items-center justify-center" data-id="element-1246">
                  <ZapIcon className="w-5 h-5 text-violet-electric" data-id="element-1247" />
                </div>
                <div data-id="element-1248">
                  <h3 className="text-lg font-semibold text-white" data-id="element-1249">
                    Thruline Core
                  </h3>
                  <p className="text-violet-electric text-xs font-medium" data-id="element-1250">
                    You're here
                  </p>
                </div>
              </div>
              <p className="text-white/55 text-sm leading-relaxed mb-4" data-id="element-1251">
                A custom-developed ERP you own and evolve. Thruline helps you
                plan and build it. You understand it, modify it, and grow it.
              </p>
              <ul className="space-y-2" data-id="element-1252">
                {['Thruline builds the first version with you', 'You operate with full transparency and AI assistance', 'You make changes directly, on your timeline', 'Best for: companies that want to own it'].map((item, i) => <li key={i} className="flex items-start gap-2 text-white/45 text-sm" data-id="element-1253">
                    <CheckIcon className="w-3.5 h-3.5 text-violet-electric mt-0.5 flex-shrink-0" data-id="element-1254" />
                    {item}
                  </li>)}
              </ul>
            </motion.div>

            {/* Enterprise Card */}
            <motion.div variants={fadeUp} className="rounded-2xl border border-white/[0.06] bg-white/[0.03] p-8" data-id="element-1255">
              <div className="flex items-center gap-3 mb-4" data-id="element-1256">
                <div className="w-10 h-10 rounded-xl bg-blue-400/15 flex items-center justify-center" data-id="element-1257">
                  <BuildingIcon className="w-5 h-5 text-blue-400" data-id="element-1258" />
                </div>
                <h3 className="text-lg font-semibold text-white" data-id="element-1259">
                  Thruline Enterprise
                </h3>
              </div>
              <p className="text-white/55 text-sm leading-relaxed mb-4" data-id="element-1260">
                A custom-configured ERP, built and maintained by Thruline. We
                handle the system. You handle the business.
              </p>
              <ul className="space-y-2 mb-6" data-id="element-1261">
                {['Thruline builds the entire system', 'Thruline operates and maintains as managed service', 'Changes handled by Thruline\'s team', 'Best for: companies that want it done'].map((item, i) => <li key={i} className="flex items-start gap-2 text-white/45 text-sm" data-id="element-1262">
                    <CheckIcon className="w-3.5 h-3.5 text-blue-400 mt-0.5 flex-shrink-0" data-id="element-1263" />
                    {item}
                  </li>)}
              </ul>
              <a href="#/solutions/enterprise" className="inline-flex items-center gap-2 text-blue-400 text-sm font-medium hover:underline" data-id="element-1264">
                Learn about Enterprise
                <ArrowRightIcon className="w-3.5 h-3.5" data-id="element-1265" />
              </a>
            </motion.div>
          </motion.div>

          <motion.p initial={{
          opacity: 0
        }} whileInView={{
          opacity: 1
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5,
          delay: 0.2
        }} className="text-white/35 text-sm text-center leading-relaxed max-w-2xl mx-auto" data-id="element-1266">
            Some customers start with Core and bring in Thruline for
            Enterprise-style builds on major expansions. Others start with
            Enterprise and transition to Core as their team builds confidence.
            The two products complement each other.
          </motion.p>
        </div>
      </section>

      {/* ── SECTION 10: BOTTOM CTA ── */}
      <section className="relative w-full py-24 px-6 overflow-hidden" data-id="element-1267">
        <div className="absolute inset-0" style={{
        background: 'linear-gradient(135deg, #0D0B2E 0%, #1A1452 30%, #6C5CE7 70%, #C850C0 100%)'
      }} data-id="element-1268" />
        <div className="absolute inset-0 opacity-[0.04] pointer-events-none" style={{
        backgroundImage: "url(\"data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noise)'/%3E%3C/svg%3E\")"
      }} data-id="element-1269" />

        <motion.div className="relative z-10 max-w-3xl mx-auto text-center" initial={{
        opacity: 0,
        y: 24
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true,
        margin: '-80px'
      }} transition={{
        duration: 0.6
      }} data-id="element-1270">
          <span className="text-white/60 text-xs font-semibold tracking-[0.2em] uppercase block mb-4" data-id="element-1271">
            Get Started
          </span>
          <h2 className="text-3xl sm:text-5xl font-bold text-white tracking-tight mb-6" data-id="element-1272">
            Your operation is ready.
            <br data-id="element-1273" />
            Let's build the system it deserves.
          </h2>
          <p className="text-white/60 text-lg mb-10 max-w-xl mx-auto" data-id="element-1274">
            Discovery is free. We'll read your documents, map your processes,
            and show you what Core looks like for your business.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4" data-id="element-1275">
            <a href="https://calendly.com/alex-thruline/learn-about-thruline" className="inline-flex items-center gap-2 px-8 py-3.5 rounded-full bg-white text-navy font-semibold text-sm hover:bg-white/90 transition-all hover:shadow-lg hover:shadow-white/10" data-id="element-1276">
              Start with Discovery
              <ArrowRightIcon className="w-4 h-4" data-id="element-1277" />
            </a>
            <a href="https://calendly.com/alex-thruline/learn-about-thruline" className="inline-flex items-center px-8 py-3.5 rounded-full border border-white/30 text-white font-medium text-sm hover:border-white/60 transition-all" data-id="element-1278">
              Talk to our team
            </a>
          </div>
        </motion.div>
      </section>
    </div>;
}